import { Team, Player, Assessment, AssessmentType } from '../types';

// Default seed data
const DEFAULT_TEAMS: Team[] = [
  {
    id: '44444444-4444-4444-4444-444444444444',
    name: 'Under 12 Lions',
    ageGroup: 'U12',
    season: '2024/25',
    coachId: '11111111-1111-1111-1111-111111111111',
    clubId: '88888888-8888-8888-8888-888888888888',
    assessmentInterval: 6,
    createdAt: new Date(),
    updatedAt: new Date()
  }
];

const DEFAULT_PLAYERS: Player[] = [
  {
    id: '55555555-5555-5555-5555-555555555555',
    firstName: 'Tommy',
    lastName: 'Smith',
    dateOfBirth: new Date('2012-05-15'),
    position: 'Forward',
    fanNumber: 'FA123456',
    teamId: '44444444-4444-4444-4444-444444444444',
    parentIds: ['33333333-3333-3333-3333-333333333333'],
    isActive: true,
    createdAt: new Date(),
    updatedAt: new Date()
  },
  {
    id: '66666666-6666-6666-6666-666666666666',
    firstName: 'Sarah',
    lastName: 'Johnson',
    dateOfBirth: new Date('2012-08-22'),
    position: 'Midfield',
    fanNumber: 'FA789012',
    teamId: '44444444-4444-4444-4444-444444444444',
    parentIds: ['33333333-3333-3333-3333-333333333333'],
    isActive: true,
    createdAt: new Date(),
    updatedAt: new Date()
  }
];

const DEFAULT_ASSESSMENTS: Assessment[] = [
  {
    id: '77777777-7777-7777-7777-777777777777',
    playerId: '55555555-5555-5555-5555-555555555555',
    assessorId: '11111111-1111-1111-1111-111111111111',
    type: AssessmentType.INITIAL,
    isComplete: true,
    assessmentDate: new Date(),
    scores: {
      technical: {
        ball_control: 7,
        passing_accuracy: 6,
        positioning: 5,
        tactical_awareness: 6,
        shooting: 8,
        dribbling: 7
      },
      physical: {
        speed: 8,
        endurance: 6,
        strength: 5,
        agility: 7,
        coordination: 6,
        balance: 7
      },
      psychological: {
        confidence: 7,
        resilience: 6,
        decision_making: 5,
        concentration: 6,
        motivation: 8,
        composure: 6
      },
      social: {
        teamwork: 8,
        communication: 6,
        respect: 9,
        leadership: 5,
        coachability: 8,
        sportsmanship: 9
      }
    },
    notes: 'Initial assessment - shows good potential in attacking play and social skills.',
    createdAt: new Date(),
    updatedAt: new Date()
  }
];

// Storage keys
const TEAMS_STORAGE_KEY = 'mockTeams';
const PLAYERS_STORAGE_KEY = 'mockPlayers';
const ASSESSMENTS_STORAGE_KEY = 'mockAssessments';

// Helper functions for localStorage persistence
const loadFromStorage = <T>(key: string, defaultData: T[]): T[] => {
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Convert date strings back to Date objects
      return parsed.map((item: any) => ({
        ...item,
        createdAt: new Date(item.createdAt),
        updatedAt: new Date(item.updatedAt),
        dateOfBirth: item.dateOfBirth ? new Date(item.dateOfBirth) : undefined,
        assessmentDate: item.assessmentDate ? new Date(item.assessmentDate) : undefined,
      }));
    }
  } catch (error) {
    console.warn(`Failed to load ${key} from localStorage:`, error);
  }
  return [...defaultData];
};

const saveToStorage = <T>(key: string, data: T[]): void => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.warn(`Failed to save ${key} to localStorage:`, error);
  }
};

// Mock data arrays with localStorage persistence
export const MOCK_TEAMS: Team[] = loadFromStorage(TEAMS_STORAGE_KEY, DEFAULT_TEAMS);
export const MOCK_PLAYERS: Player[] = loadFromStorage(PLAYERS_STORAGE_KEY, DEFAULT_PLAYERS);
export const MOCK_ASSESSMENTS: Assessment[] = loadFromStorage(ASSESSMENTS_STORAGE_KEY, DEFAULT_ASSESSMENTS);

// Functions to save data to localStorage
export const saveMockTeams = (): void => {
  saveToStorage(TEAMS_STORAGE_KEY, MOCK_TEAMS);
};

export const saveMockPlayers = (): void => {
  saveToStorage(PLAYERS_STORAGE_KEY, MOCK_PLAYERS);
};

export const saveMockAssessments = (): void => {
  saveToStorage(ASSESSMENTS_STORAGE_KEY, MOCK_ASSESSMENTS);
};

// Utility functions for mock data access control
export const isOriginalMockUser = (): boolean => {
  const currentUserEmail = localStorage.getItem('currentUserEmail');
  return currentUserEmail === 'coach@test.com';
};

export const getCurrentMockUserId = (): string => {
  const currentUserEmail = localStorage.getItem('currentUserEmail');
  
  if (currentUserEmail === 'coach@test.com') {
    return '11111111-1111-1111-1111-111111111111';
  }
  
  // For new users, create a persistent mock user ID
  const mockUserIdKey = `mockUserId_${currentUserEmail}`;
  let mockUserId = localStorage.getItem(mockUserIdKey);
  
  if (!mockUserId) {
    mockUserId = `mock-user-${Date.now()}`;
    localStorage.setItem(mockUserIdKey, mockUserId);
  }
  
  return mockUserId;
};

export const hasAccessToPreSeededData = (): boolean => {
  return isOriginalMockUser();
};